import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import clsx from 'clsx';
import { GripVertical, Trash2, Plus, Play, Search, AlertCircle, ChevronUp, ChevronDown } from 'lucide-react';
import PageLayout from '@components/layout/PageLayout';
import DetailPageHeader from '@components/common/DetailPageHeader';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Select from '@components/common/Select';
import Checkbox from '@components/common/Checkbox';
import { LoadingState } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import { RZTable } from '@components/common/table/RZTable';
import { SqlView } from '@components/common/SqlView';
import { Pagination } from '@components/common/table/Pagination';
import GenericModal from '@components/common/modals/GenericModal';
import { FilterValueInput } from '@components/features/queries';
import { useQuerySchema } from '@hooks/useQuerySchema';
import { useQueryList } from '@hooks/useQueryList';
import { usePermissions } from '@contexts/AuthContext';
import { getApiErrorMessage } from '@utils/apiError';
import { validateFilters } from '@utils/queryFilterValidation';
import { queryService } from '@services';
import { OPERATORS_BY_FIELD_TYPE, OPERATOR_LABELS, QUERY_PAGE_SIZES } from '@app-types';
import type {
  QueryColumn,
  QueryFilter,
  QueryField,
  QueryFieldType,
  QueryFilterOperator,
  QueryCellValue,
  QueryType,
  QuerySort,
  QueryResultRow,
  PreviewQueryRequest,
  QueryPreviewResponse,
  TableColumn,
  CreateQueryRequest
} from '@app-types';
import styles from './QueryBuilderPage.module.css';

/** Which action raised the current validation/error, so the message title matches it (#865). */
type ErrorContext = 'save' | 'run';

const displayValue = (value: QueryCellValue): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

interface PreviewResultsPanelProps {
  previewResults: QueryPreviewResponse;
  previewColumns: TableColumn[];
  previewSort: QuerySort | null;
  previewPage: number;
  previewPageSize: number;
  isPreviewing: boolean;
  onSort: (columnKey: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

/** Preview modal's results panel (SQL + table + pagination), once results have loaded. */
const PreviewResultsPanel: React.FC<PreviewResultsPanelProps> = ({
  previewResults,
  previewColumns,
  previewSort,
  previewPage,
  previewPageSize,
  isPreviewing,
  onSort,
  onPageChange,
  onPageSizeChange
}) => (
  <>
    <p className={styles.previewNote}>
      {previewResults.totalCount} result{previewResults.totalCount === 1 ? '' : 's'} · not saved
    </p>
    {previewResults.sql && <SqlView sql={previewResults.sql} title="Query SQL" />}
    {/* The table fills the modal width so a 1–2 column preview stretches
        to use the space (#902); each column carries a min-width (in CSS),
        so a wide query overflows this scroll area and scrolls horizontally
        instead. The modal is a fixed width, so the layout stays stable
        across pages. */}
    <div className={styles.previewTableScroll}>
      <RZTable<QueryResultRow>
        data={previewResults.rows}
        columns={previewColumns}
        getRowKey={(row) => previewResults.rows.indexOf(row)}
        renderCell={(column, row) => displayValue(row[column.key])}
        sorting={{
          sortConfig: {
            field: previewSort?.field ?? '',
            direction: previewSort?.direction === 'desc' ? 'DESC' : 'ASC'
          },
          onSort,
          isSorted: (columnKey) => columnKey === previewSort?.field
        }}
        state={{
          isLoading: isPreviewing && previewResults.rows.length === 0,
          isFetching: isPreviewing,
          isEmpty: previewResults.totalCount === 0,
          emptyTitle: 'No results',
          emptyDescription: 'This query returned no rows.'
        }}
      />
    </div>
    {/* Pagination stays pinned below the scroll area. */}
    <Pagination
      currentPage={previewPage}
      totalPages={Math.max(1, Math.ceil(previewResults.totalCount / previewPageSize))}
      totalItems={previewResults.totalCount}
      pageSize={previewPageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      itemName="rows"
      pageSizeOptions={[...QUERY_PAGE_SIZES]}
    />
  </>
);

const QueryBuilderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = usePermissions();
  const isEdit = Boolean(id);
  const queryId = id ? Number(id) : undefined;

  // Where Back and a successful Save should return to. The opener passes this in
  // nav state — the query's details page when editing came from there, or the
  // list when it came from the list. Falling back to the details in edit mode
  // (you're editing that query) or the list when creating covers a direct visit.
  const returnTo =
    (location.state as { returnTo?: string } | null)?.returnTo ??
    (isEdit ? `/queries/${queryId}/results` : '/queries');

  const { entities: schemaEntities, relationships, isLoading: schemaLoading } = useQuerySchema();
  const { createQuery, updateQuery, isCreating, isUpdating } = useQueryList();

  const { data: existing, isLoading: existingLoading } = useQuery({
    queryKey: ['query', queryId],
    queryFn: () => queryService.getById(queryId as number),
    enabled: isEdit && queryId !== undefined
  });

  const [name, setName] = useState('');
  const [type, setType] = useState<QueryType>('personal');
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [columns, setColumns] = useState<QueryColumn[]>([]);
  const [filters, setFilters] = useState<QueryFilter[]>([]);
  // Stable per-row React keys for the filters list — filter objects carry no id
  // of their own and can be structurally identical (e.g. two blank "equals"
  // rows), so array index is the only alternative and breaks row identity
  // across add/remove. Kept in lockstep with `filters` (same length/order).
  const [filterKeys, setFilterKeys] = useState<number[]>([]);
  const nextFilterKey = useRef(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [errorContext, setErrorContext] = useState<ErrorContext>('save');

  // Preview state (US-08 "Run", #863). Running snapshots the current definition
  // and opens a modal; the form stays put. Pagination/sort are reactive query
  // params, so changing them refetches without leaving the builder.
  const [previewDef, setPreviewDef] = useState<Pick<PreviewQueryRequest, 'entities' | 'columns' | 'filters'> | null>(
    null
  );
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageSize, setPreviewPageSize] = useState<number>(QUERY_PAGE_SIZES[0]);
  const [previewSort, setPreviewSort] = useState<QuerySort | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const {
    data: previewResults,
    isFetching: isPreviewing,
    error: previewError
  } = useQuery({
    queryKey: ['queryPreview', previewDef, previewPage, previewPageSize, previewSort],
    queryFn: () =>
      queryService.preview({
        ...(previewDef as Pick<PreviewQueryRequest, 'entities' | 'columns' | 'filters'>),
        page: previewPage,
        pageSize: previewPageSize,
        sort: previewSort ?? undefined
      }),
    enabled: isPreviewOpen && previewDef !== null,
    staleTime: 0,
    // Keep the current rows on screen while the next page/sort loads, so the
    // table never unmounts (no disappear/reappear) during in-modal navigation.
    placeholderData: keepPreviousData
  });

  // Drag-and-drop reordering of the chosen columns: the row currently being
  // dragged, and the row it's hovering over (used to draw the drop indicator).
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Hydrate the form when editing an existing query.
  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setType(existing.type);
      setSelectedEntities(existing.entities);
      setColumns(existing.columns);
      setFilters(existing.filters);
      setFilterKeys(existing.filters.map(() => nextFilterKey.current++));
    }
  }, [existing]);

  // Editing a Global query is Admin-only. Block the edit route for non-admins
  // (e.g. /queries/1/edit reached directly) instead of opening a form whose
  // submit the backend would reject (#895).
  useEffect(() => {
    if (existing?.type === 'global' && !permissions.isAdmin) {
      navigate(`/queries/${existing.id}/results`, { replace: true });
    }
  }, [existing, permissions.isAdmin, navigate]);

  const fieldType = (entity: string, field: string): QueryFieldType => {
    const found = schemaEntities.find((e) => e.key === entity)?.fields.find((f) => f.key === field);
    return found?.type ?? 'text';
  };

  const fieldOptions = (entity: string, field: string): string[] =>
    schemaEntities.find((e) => e.key === entity)?.fields.find((f) => f.key === field)?.options ?? [];

  const fieldFor = (entity: string, field: string): QueryField | undefined =>
    schemaEntities.find((e) => e.key === entity)?.fields.find((f) => f.key === field);

  // Default a new/changed filter to the first filterable field (#919): derived
  // to-many aggregates are filterable, but some fields may opt out via the flag.
  const firstFilterableField = (entity: string): string =>
    schemaEntities.find((e) => e.key === entity)?.fields.find((f) => f.filterable !== false)?.key ?? '';

  // Seed a filter's value so the control's displayed default is actually stored.
  // Boolean/enum dropdowns show a first option that the user may never click —
  // without seeding, the value stays undefined and the backend treats the filter
  // as "no filter", returning the whole dataset (bug 868). Text/number/date start
  // empty so the user must type a value.
  const defaultValueForField = (entity: string, field: string): QueryCellValue | undefined => {
    const type = fieldType(entity, field);
    if (type === 'boolean') return true;
    if (type === 'enum') return fieldOptions(entity, field)[0];
    return undefined;
  };

  // When the field (or entity) changes, the previously chosen operator may no
  // longer be valid for the new field type (e.g. "Date range" on a text field),
  // which would leave stale inputs on screen (lingering date-range pickers).
  // Keep the operator if it's still valid, otherwise fall back to the first one,
  // and drop any previously entered values so the inputs match the new field.
  const reconcileForField = (entity: string, field: string, current: QueryFilterOperator): Partial<QueryFilter> => {
    const operators = OPERATORS_BY_FIELD_TYPE[fieldType(entity, field)];
    return {
      operator: operators.includes(current) ? current : operators[0],
      value: defaultValueForField(entity, field),
      valueTo: undefined
    };
  };

  const entityLabel = (key: string): string => schemaEntities.find((e) => e.key === key)?.label ?? key;
  const fieldLabel = (entity: string, field: string): string =>
    schemaEntities.find((e) => e.key === entity)?.fields.find((f) => f.key === field)?.label ?? field;

  // Undirected adjacency of joinable entities, derived from schema relationships.
  const adjacency = useMemo(() => {
    const map = new Map<string, Set<string>>();
    const link = (a: string, b: string): void => {
      const set = map.get(a) ?? new Set<string>();
      set.add(b);
      map.set(a, set);
    };
    relationships.forEach(({ from, to }) => {
      link(from, to);
      link(to, from);
    });
    return map;
  }, [relationships]);

  // An entity is selectable only if it can connect to the current selection
  // (directly related to a selected entity). This blocks unjoinable combinations
  // and forces the user to add a bridge entity (e.g. Contracts / Division) first.
  const isEntityEnabled = (key: string): boolean => {
    if (selectedEntities.length === 0) return true;
    if (selectedEntities.includes(key)) return true;
    return selectedEntities.some((selected) => adjacency.get(selected)?.has(key) ?? false);
  };

  // Why a candidate entity is disabled, or undefined if it can be added.
  // Already-selected entities are never disabled (they can still be unchecked).
  // Layers the adjacency rule (isEntityEnabled) with the invalid-combination
  // rules the backend now rejects (bug 941): Contacts+Contracts can't be counted
  // exactly (many-to-many through Division), and at most one of Assessments/
  // Meetings may accompany Contracts (two to-many relations = cartesian product).
  const disabledReason = (key: string): string | undefined => {
    if (selectedEntities.includes(key)) return undefined;
    if (!isEntityEnabled(key)) {
      return 'Not directly related to the selected entities — add a connecting entity first';
    }
    const contactContractPair = ['contact', 'contract'];
    if (contactContractPair.includes(key) && selectedEntities.some((e) => contactContractPair.includes(e))) {
      return "Contacts and Contracts can't be combined — query Divisions and Contacts on their own";
    }
    const assessmentMeetingPair = ['assessment', 'meeting'];
    if (
      assessmentMeetingPair.includes(key) &&
      selectedEntities.includes('contract') &&
      assessmentMeetingPair.some((e) => selectedEntities.includes(e))
    ) {
      return 'With Contracts, select only one of Assessments or Meetings';
    }
    return undefined;
  };

  // Removing an entity also drops any columns/filters that referenced it, so the
  // form never carries a selection the user can no longer see or reach.
  const removeEntityReferences = (key: string): void => {
    setColumns((cols) => cols.filter((c) => c.entity !== key).map((c, idx) => ({ ...c, order: idx })));
    // `filters` and `filterKeys` are index-aligned, so both must drop the same
    // rows. Each updater decides per row from the state React hands it, rather
    // than sharing indices computed from the render closure's `filters` — that
    // snapshot can be stale under batching, which would index the wrong rows and
    // leave `undefined` entries. `keysToDrop` is captured from the same `filters`
    // render that produced the current `filterKeys`, so the two stay in lockstep.
    const keysToDrop = new Set(filterKeys.filter((_, i) => filters[i]?.entity === key));
    setFilterKeys((keys) => keys.filter((k) => !keysToDrop.has(k)));
    setFilters((fs) => fs.filter((f) => f.entity !== key));
  };

  const toggleEntity = (key: string): void => {
    const isRemoving = selectedEntities.includes(key);
    if (isRemoving) removeEntityReferences(key);
    setSelectedEntities((prev) => (isRemoving ? prev.filter((e) => e !== key) : [...prev, key]));
  };

  const isColumnSelected = (entity: string, field: string): boolean =>
    columns.some((c) => c.entity === entity && c.field === field);

  const toggleColumn = (entity: string, field: string): void => {
    setColumns((prev) => {
      if (prev.some((c) => c.entity === entity && c.field === field)) {
        return prev.filter((c) => !(c.entity === entity && c.field === field)).map((c, idx) => ({ ...c, order: idx }));
      }
      return [...prev, { entity, field, order: prev.length }];
    });
  };

  const orderedColumns = useMemo(() => [...columns].sort((a, b) => a.order - b.order), [columns]);

  // Preview table columns mirror the selected output columns, in order.
  const previewColumns: TableColumn[] = useMemo(
    () =>
      orderedColumns.map((col) => ({
        key: `${col.entity}.${col.field}`,
        label: `${fieldLabel(col.entity, col.field)} (${entityLabel(col.entity)})`,
        defaultVisible: true,
        // To-many derived aggregates can't be sorted server-side (#919).
        sortable: fieldFor(col.entity, col.field)?.sortable !== false
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderedColumns, schemaEntities]
  );

  // Move the column at `from` to position `to`, re-sequencing `order` to match.
  const reorderColumns = (from: number, to: number): void => {
    if (from === to) return;
    setColumns((prev) => {
      const next = [...prev].sort((a, b) => a.order - b.order);
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((c, idx) => ({ ...c, order: idx }));
    });
  };

  const handleDrop = (targetIndex: number): void => {
    if (dragIndex !== null) reorderColumns(dragIndex, targetIndex);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const addFilter = (): void => {
    const entity = selectedEntities[0];
    if (!entity) return;
    const field = firstFilterableField(entity);
    setFilters((prev) => [
      ...prev,
      { entity, field, operator: 'equals', value: defaultValueForField(entity, field) }
    ]);
    setFilterKeys((keys) => [...keys, nextFilterKey.current++]);
  };

  const updateFilter = (index: number, patch: Partial<QueryFilter>): void => {
    setFilters((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };

  const removeFilter = (index: number): void => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
    setFilterKeys((keys) => keys.filter((_, i) => i !== index));
  };

  const typeOptions = useMemo(() => {
    const options = [{ value: 'personal', label: 'Personal' }];
    if (permissions.isAdmin) options.push({ value: 'global', label: 'Global' });
    return options;
  }, [permissions.isAdmin]);

  // Validate filter values against their field types, mirroring the backend so
  // the user gets immediate feedback instead of an empty result set (bug 887
  // inverted date range, 888 non-numeric value, 891 empty value).
  const filterValidationError = (): string | null => validateFilters(filters, schemaEntities);

  /**
   * Validate the current definition. Returns an error message, or null if valid.
   * `requireName` is false for Run — a preview is never persisted, so it needs
   * no name (#863/#865).
   */
  const validateDefinition = (requireName: boolean): string | null => {
    if (requireName && !name.trim()) return 'Query name is required';
    if (selectedEntities.length === 0) return 'Select at least one entity';
    if (columns.length === 0) return 'Select at least one column';
    // Contacts are roleless users scoped by Division — they can't be queried
    // without it, even on their own (bug 892: avoids leaking unscoped users).
    if (selectedEntities.includes('contact') && !selectedEntities.includes('division')) {
      return 'Add Division to connect these entities';
    }
    // Multiple entities join through Contracts, except the standalone
    // Division (+Contacts) hub.
    const isDivisionHub = selectedEntities.every((e) => e === 'division' || e === 'contact');
    if (selectedEntities.length > 1 && !selectedEntities.includes('contract') && !isDivisionHub) {
      return 'Add Contracts to connect these entities';
    }
    // Contacts join a Contract only through a Division holding many Contracts
    // (many-to-many), so the combination can't be counted/paginated exactly (941).
    if (selectedEntities.includes('contract') && selectedEntities.includes('contact')) {
      return 'Contacts cannot be combined with Contracts; query Divisions and Contacts on their own';
    }
    // Two to-many relations alongside Contracts are a cartesian product with no
    // meaningful row count — at most one of Assessments/Meetings is allowed (941).
    if (
      selectedEntities.includes('contract') &&
      selectedEntities.includes('assessment') &&
      selectedEntities.includes('meeting')
    ) {
      return 'Select at most one of Assessments or Meetings together with Contracts';
    }
    return filterValidationError();
  };

  const fail = (context: ErrorContext, message: string): void => {
    setErrorContext(context);
    setFormError(message);
  };

  /**
   * Validate + persist the query (create or update). Returns the saved query id,
   * or null when validation fails (formError is set).
   */
  const persist = async (): Promise<number | null> => {
    const error = validateDefinition(true);
    if (error) {
      fail('save', error);
      return null;
    }
    setFormError(null);

    const payload: CreateQueryRequest = {
      name: name.trim(),
      type,
      entities: selectedEntities,
      columns: orderedColumns,
      filters
    };

    if (isEdit && queryId !== undefined) {
      await updateQuery({ id: queryId, payload });
      return queryId;
    }
    const created = await createQuery(payload);
    return created.data.id;
  };

  const handleSave = async (): Promise<void> => {
    try {
      const savedId = await persist();
      if (savedId !== null) navigate(returnTo);
    } catch (err) {
      // Surface the backend's message (e.g. the duplicate-name conflict) so the
      // user understands a query with that name already exists.
      fail('save', getApiErrorMessage(err, 'Failed to save query'));
    }
  };

  // "Run" previews results without saving — only "Save Query" creates the query
  // (#863). No name is required to preview (#865). Snapshots the definition and
  // opens the modal; the useQuery above fetches and refetches on page/sort change.
  const handleRun = (): void => {
    const error = validateDefinition(false);
    if (error) {
      fail('run', error);
      return;
    }
    setFormError(null);
    setPreviewSort(null);
    setPreviewPage(1);
    setPreviewDef({ entities: selectedEntities, columns: orderedColumns, filters });
    setIsPreviewOpen(true);
  };

  const handlePreviewPageChange = (page: number): void => setPreviewPage(page);

  const handlePreviewPageSizeChange = (size: number): void => {
    setPreviewPageSize(size);
    setPreviewPage(1);
  };

  const handlePreviewSort = (columnKey: string): void => {
    setPreviewSort((prev) => ({
      field: columnKey,
      direction: prev?.field === columnKey && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setPreviewPage(1);
  };

  const renderPreviewBody = (): React.ReactNode => {
    if (previewError) {
      return (
        <ErrorState
          title="Failed to run query"
          message={getApiErrorMessage(previewError, 'An error occurred while running the query.')}
        />
      );
    }
    if (!previewResults) {
      return <LoadingState message="Running query..." />;
    }
    return (
      <PreviewResultsPanel
        previewResults={previewResults}
        previewColumns={previewColumns}
        previewSort={previewSort}
        previewPage={previewPage}
        previewPageSize={previewPageSize}
        isPreviewing={isPreviewing}
        onSort={handlePreviewSort}
        onPageChange={handlePreviewPageChange}
        onPageSizeChange={handlePreviewPageSizeChange}
      />
    );
  };

  if (schemaLoading || (isEdit && existingLoading)) {
    return (
      <PageLayout>
        <LoadingState message="Loading query builder..." spinnerSize="large" />
      </PageLayout>
    );
  }

  // Don't paint the edit form for a non-admin opening a Global query — the effect
  // above redirects, and this avoids a one-frame flash of the (un-submittable)
  // form before that happens (#895).
  if (isEdit && existing?.type === 'global' && !permissions.isAdmin) {
    return (
      <PageLayout>
        <LoadingState message="Loading query builder..." spinnerSize="large" />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <DetailPageHeader
        icon={<Search size={24} />}
        title={isEdit ? 'Edit Query' : 'Create Query'}
        backLabel="Go Back"
        onBack={() => navigate(returnTo)}
        actions={
          <>
            <Button variant="secondary" size="sm" loading={isPreviewing} onClick={handleRun}>
              <Play size={16} />
              Run
            </Button>
            <Button variant="primary" size="sm" loading={isCreating || isUpdating} onClick={handleSave}>
              Save Query
            </Button>
          </>
        }
      />
      <div className={styles.builder}>
        {formError && (
          <div className={styles.errorBanner} role="alert">
            <AlertCircle size={18} className={styles.errorBannerIcon} aria-hidden />
            <span>
              <span className={styles.errorBannerTitle}>
                {errorContext === 'run' ? 'Cannot run' : 'Cannot save'}:
              </span>{' '}
              {formError}
            </span>
          </div>
        )}

        {/* Meta */}
        <section className={styles.section}>
          <div className={styles.metaRow}>
            <Input
              label="Name"
              required
              value={name}
              maxLength={255}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Active Tier 1 Contracts"
              wrapperClassName={styles.nameInput}
            />
            {/* Only Admins can create Global queries; for everyone else the type is
                fixed to Personal, so the single-option selector is hidden (#896). */}
            {permissions.isAdmin && (
              <Select
                label="Type"
                options={typeOptions}
                value={type}
                onChange={(value) => setType(value as QueryType)}
              />
            )}
          </div>
        </section>

        <div className={styles.grid}>
          {/* Left column: field selection */}
          <div className={styles.col}>
            {/* Entities */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>1. Entities</h2>
              <div className={styles.checkboxGrid}>
                {schemaEntities.map((entity) => {
                  const reason = disabledReason(entity.key);
                  return (
                    <Checkbox
                      key={entity.key}
                      label={entity.label}
                      checked={selectedEntities.includes(entity.key)}
                      disabled={reason !== undefined}
                      onChange={() => toggleEntity(entity.key)}
                      title={reason}
                    />
                  );
                })}
              </div>
              {selectedEntities.length > 0 && (
                <p className={styles.hint}>Greyed-out entities can’t be joined to your current selection.</p>
              )}
            </section>

            {/* Columns */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>2. Columns</h2>
              {selectedEntities.length === 0 && <p className={styles.hint}>Select an entity to choose columns.</p>}
              {selectedEntities.map((entityKey) => (
                <div key={entityKey} className={styles.entityColumns}>
                  <h3 className={styles.entitySubtitle}>{entityLabel(entityKey)}</h3>
                  <div className={styles.checkboxGrid}>
                    {schemaEntities
                      .find((e) => e.key === entityKey)
                      ?.fields.map((field) => (
                        <Checkbox
                          key={`${entityKey}.${field.key}`}
                          label={field.label}
                          checked={isColumnSelected(entityKey, field.key)}
                          onChange={() => toggleColumn(entityKey, field.key)}
                        />
                      ))}
                  </div>
                </div>
              ))}
            </section>
          </div>

          {/* Right column: column order + filters */}
          <div className={styles.col}>
            {/* Column order */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>3. Column order</h2>
              {orderedColumns.length === 0 && (
                <p className={styles.hint}>Select columns to arrange their order.</p>
              )}
              {orderedColumns.length > 0 && (
                <p className={styles.hint}>Drag the rows to change the column order.</p>
              )}
              {orderedColumns.map((col, index) => (
                  <div
                    key={`${col.entity}.${col.field}`}
                    className={clsx(
                      styles.orderItem,
                      dragIndex === index && styles.dragging,
                      dragOverIndex === index && dragIndex !== index && styles.dragOver
                    )}
                    draggable
                    onDragStart={() => setDragIndex(index)}
                    onDragEnter={() => setDragOverIndex(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleDrop(index);
                    }}
                    onDragEnd={() => {
                      setDragIndex(null);
                      setDragOverIndex(null);
                    }}
                  >
                    <span className={styles.orderLabel}>
                      <GripVertical size={16} className={styles.dragHandle} aria-hidden />
                      <span className={styles.orderIndex}>{index + 1}</span>
                      {fieldLabel(col.entity, col.field)} <em>({entityLabel(col.entity)})</em>
                    </span>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => reorderColumns(index, index - 1)}
                      disabled={index === 0}
                      title="Move up"
                      aria-label={`Move ${fieldLabel(col.entity, col.field)} up`}
                    >
                      <ChevronUp size={16} />
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => reorderColumns(index, index + 1)}
                      disabled={index === orderedColumns.length - 1}
                      title="Move down"
                      aria-label={`Move ${fieldLabel(col.entity, col.field)} down`}
                    >
                      <ChevronDown size={16} />
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      className={styles.removeButton}
                      onClick={() => toggleColumn(col.entity, col.field)}
                      title="Remove column"
                      aria-label="Remove column"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
            </section>

            {/* Filters */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>4. Filters</h2>
              {filters.map((filter, index) => {
                const entityFields = schemaEntities.find((e) => e.key === filter.entity)?.fields ?? [];
                const operators = OPERATORS_BY_FIELD_TYPE[fieldType(filter.entity, filter.field)];
                return (
                  <div key={filterKeys[index]} className={styles.filterRow}>
                    <Select
                      options={selectedEntities.map((key) => ({ value: key, label: entityLabel(key) }))}
                      value={filter.entity}
                      onChange={(value) => {
                        const field = firstFilterableField(value);
                        updateFilter(index, {
                          entity: value,
                          field,
                          ...reconcileForField(value, field, filter.operator)
                        });
                      }}
                    />
                    <Select
                      options={entityFields
                        .filter((f) => f.filterable !== false)
                        .map((f) => ({ value: f.key, label: f.label }))}
                      value={filter.field}
                      onChange={(value) =>
                        updateFilter(index, {
                          field: value,
                          ...reconcileForField(filter.entity, value, filter.operator)
                        })
                      }
                    />
                    <Select
                      options={operators.map((op) => ({ value: op, label: OPERATOR_LABELS[op] }))}
                      value={filter.operator}
                      onChange={(value) => updateFilter(index, { operator: value as QueryFilterOperator })}
                    />
                    <FilterValueInput
                      filter={filter}
                      fieldType={fieldType(filter.entity, filter.field)}
                      options={fieldOptions(filter.entity, filter.field)}
                      onChange={(patch) => updateFilter(index, patch)}
                    />
                    <Button
                      size="xs"
                      variant="ghost"
                      className={styles.removeButton}
                      onClick={() => removeFilter(index)}
                      title="Remove filter"
                      aria-label="Remove filter"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                );
              })}
              <Button variant="secondary" onClick={addFilter} disabled={selectedEntities.length === 0}>
                <Plus size={16} />
                Add Filter
              </Button>
            </section>
          </div>
        </div>

      </div>

      <GenericModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title="Preview"
        dataTestId="query-preview-modal"
        contentClassName={styles.previewModalContent}
        bodyClassName={styles.previewModalBody}
        footer={
          <Button variant="secondary" onClick={() => setIsPreviewOpen(false)}>
            Close
          </Button>
        }
      >
        {renderPreviewBody()}
      </GenericModal>
    </PageLayout>
  );
};

export default QueryBuilderPage;
